/**
 * @file Chat Message Response Union Type
 * @description Manual type alias for discriminated union of all chat message types
 * @see apps/backend/src/chat/presentation/dto/chat-message.dto.ts (lines 487-492)
 *
 * **Why manual?**
 * - Backend defines union type as TypeScript `type` (not Swagger component)
 * - Orval doesn't generate union types from inline oneOf schemas
 * - This file provides the missing type for @semo/shared-chat package
 *
 * **Pattern**: Discriminated Union with messageType as discriminator
 */

import type {
  TextMessageResponseDto,
  AttachmentMessageResponseDto,
  QuoteMessageResponseDto,
  ContractMessageResponseDto,
  SystemMessageResponseDto,
  PaymentPendingMessageResponseDto,
  CancellationRequestMessageResponseDto,
  ReviewSubmittedMessageResponseDto,
  BaseChatMessageResponseDto,
} from "../types/client.schemas";
import { api } from "./api-client";

// ============================================================================
// DOCUMENT_REQUEST (계약서류 선택 요청 - c-market 연동)
// ----------------------------------------------------------------------------
// NOTE: `pnpm sync:api-types` 재생성 전에도 사용할 수 있도록 수동 정의.
// 백엔드 DocumentRequestMessageResponseDto(chat-message.dto.ts)와 동일 구조.
// Orval 재생성 후에는 client.schemas의 생성 타입으로 대체 가능하다.
// ============================================================================

/**
 * 계약서류 선택 옵션 (c-market GET /v2/contract-documents 응답 스냅샷)
 * - isDefault: 운영자 기본값(★) → 선택 화면에서 미리 체크
 * - autoGenerated=false: 시스템 미생성 → 공급사 직접 제출 안내
 * - supported=false: 생성 API 미지원 → 목록에서 감추지 말고 수기 제출 안내
 */
export interface ContractDocumentOptionDto {
  /** c-market 서류 코드 */
  paperCode: number;
  /** 서류 이름 */
  name: string;
  /** 운영자 기본값(★) 여부 */
  isDefault: boolean;
  /** 시스템 자동 생성 가능 여부 */
  autoGenerated: boolean;
  /** 전자서명 필요 여부 */
  requiresDigitalSign: boolean;
  /** 생성 API 지원 여부 */
  supported: boolean;
}

export interface DocumentRequestDataDto {
  /** 계약 ID */
  contractId: string;
  /** c-market 거래유형 (quote_requests.category_type 매핑) */
  bidType: "goods" | "service" | "construction" | "sale";
  /** 선택 가능한 계약서류 목록 */
  documents: ContractDocumentOptionDto[];
  /** 요청 상태 */
  status: "pending" | "requested";
  /** 발급 완료된 c-market 계약서류 코드 */
  issuedPaperCodes: number[];
}

/**
 * DOCUMENT_REQUEST 메시지 응답 DTO (계약서류 선택 요청)
 */
export interface DocumentRequestMessageResponseDto extends Omit<
  BaseChatMessageResponseDto,
  "messageType"
> {
  /** 메시지 타입 */
  messageType: "document_request";
  /** 계약서류 선택 요청 정보 */
  documentRequestData: DocumentRequestDataDto;
}

// ============================================================================
// CONTRACT_RESTRICTION_CONFIRM (수의계약체결제한여부 확인 요청 - paperCode 135)
// ----------------------------------------------------------------------------
// 공급사가 문항에 "예"로 응답해 제출하면 발주기관 채팅방에 남는 확인 요청 카드.
// 백엔드 ContractRestrictionConfirmMessageResponseDto(chat-message.dto.ts)와 동일 구조.
// ============================================================================

export interface ContractRestrictionConfirmDataDto {
  /** 계약 ID */
  contractId: string;
  /** 짝이 되는 [계약 서류 관리] 카드 메시지 ID */
  documentRequestMessageId: string;
  /** 8개 문항 응답 원본 ("YES" | "NO") */
  answers: string[];
  /** "예"로 응답한 문항 인덱스(0..7) */
  flaggedIndexes: number[];
  /** 공급사 제출 시각 (ISO 8601) */
  submittedAt: string;
  /** 발주기관 최종 확인 응답. 미제출이면 null */
  buyerConfirmation: "YES" | "NO" | null;
  /** buyer 확인 제출 시각 (ISO 8601). 미제출이면 null */
  confirmedAt: string | null;
}

/**
 * CONTRACT_RESTRICTION_CONFIRM 메시지 응답 DTO (수의계약 체결 여부 확인 요청)
 */
export interface ContractRestrictionConfirmMessageResponseDto extends Omit<
  BaseChatMessageResponseDto,
  "messageType"
> {
  /** 메시지 타입 */
  messageType: "contract_restriction_confirm";
  /** 수의계약 체결 제한 확인 요청 정보 */
  contractRestrictionConfirmData: ContractRestrictionConfirmDataDto;
}

export interface GeneratedContractDocumentDto {
  paperCode: number;
  name: string;
  fileUrl: string;
}

export function requestContractDocuments(
  roomId: string,
  body: {
    messageId: string;
    paperCodes: number[];
    idempotencyKey: string;
  },
): Promise<GeneratedContractDocumentDto[]> {
  return api({
    url: `/chat/rooms/${roomId}/contract-documents`,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: body,
  });
}

export interface UploadedContractDocumentDto {
  id: string;
  paperCode: number;
  name: string;
  kind: "auto" | "manual";
  status: "pending" | "completed";
  fileUrl?: string | null;
  fileName?: string | null;
  fileSize?: number | null;
  contentType?: string | null;
  uploadedAt?: string | null;
}

export interface RetireeRosterRowInput {
  name: string;
  position: string;
  joinDate?: string | null;
  retireType?: string | null;
  servicePeriod?: string | null;
  etc?: string | null;
}

export interface SubmitContractDocumentInputBody {
  idempotencyKey: string;
  contractRestrictionConfirm?: {
    /**
     * 8개 문항 응답 ("YES" | "NO"). 전 문항 "NO" 는 즉시 발급, "YES" 포함은 answers 만 저장한 뒤
     * buyer 확인(submitContractDocumentBuyerConfirmation) 후에야 실제 발급된다 (2026-08-03 정책).
     */
    answers: string[];
  };
  /**
   * c-market spec: 해당없음 = { rows: [] } (빈 배열).
   * null / 필드 생략 시 c-market 이 "확인 안 함" 으로 400 UPSTREAM_REJECTED 를 낸다.
   */
  retireeRoster?: { rows: RetireeRosterRowInput[] };
}

/**
 * Supplier — 계약 서류 documentInputs 제출 (paperCode 135/164/188/195/222).
 * 백엔드가 c-market POST 를 대행해 서류 발급 → 카드 갱신 후 최신 doc 반환.
 * paperCode 135 + "YES" 포함인 경우 answers 만 저장되고 (pending 상태 유지) buyer 확인이 별도로 필요하다.
 */
export function submitContractDocumentInput(
  roomId: string,
  paperCode: number,
  body: SubmitContractDocumentInputBody,
): Promise<{ data: UploadedContractDocumentDto }> {
  return api({
    url: `/chat/rooms/${roomId}/contract-documents/${paperCode}/submit-input`,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: body,
  });
}

/**
 * Buyer — paperCode 135 발주기관 최종 확인 응답 제출.
 * supplier 가 "YES" 를 포함해 제출한 카드에 대해 발주기관이 "예/아니오" 를 확정한다.
 * 백엔드가 저장된 answers 와 buyerConfirmation 을 묶어 c-market POST 를 태우고 서류를 발급한다.
 */
export function submitContractDocumentBuyerConfirmation(
  roomId: string,
  body: { idempotencyKey: string; buyerConfirmation: "YES" | "NO" },
): Promise<{ data: UploadedContractDocumentDto }> {
  return api({
    url: `/chat/rooms/${roomId}/contract-documents/135/buyer-confirmation`,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: body,
  });
}

/**
 * Supplier — [계약 서류 관리] 카드의 직접 제출 서류 파일 업로드.
 * 응답으로 갱신된 항목(UploadedContractDocumentDto) 를 리턴하므로 프론트에서
 * 즉시 optimistic cache patch 가 가능하다.
 */
export function uploadContractDocumentFile(
  roomId: string,
  paperCode: number,
  file: File,
): Promise<{ data: UploadedContractDocumentDto }> {
  const form = new FormData();
  form.append("file", file);
  return api({
    url: `/chat/rooms/${roomId}/contract-documents/${paperCode}/upload`,
    method: "POST",
    headers: { "Content-Type": "multipart/form-data" },
    data: form,
  });
}

/**
 * Chat Message Response DTO (Discriminated Union)
 *
 * All chat messages have a `messageType` field that determines the actual message shape.
 * Use TypeScript discriminated union pattern for type-safe message handling.
 *
 * @example
 * ```typescript
 * function handleMessage(message: ChatMessageResponseDto) {
 *   switch (message.messageType) {
 *     case 'text':
 *       console.log(message.message)  // ✅ Type-safe
 *       break
 *     case 'attachment':
 *       console.log(message.attachment.fileName)  // ✅ Type-safe
 *       break
 *     case 'quote':
 *       console.log(message.quoteData.totalAmount)  // ✅ Type-safe
 *       break
 *   }
 * }
 * ```
 */
export type ChatMessageResponseDto =
  | TextMessageResponseDto
  | AttachmentMessageResponseDto
  | QuoteMessageResponseDto
  | ContractMessageResponseDto
  | SystemMessageResponseDto
  | PaymentPendingMessageResponseDto
  | CancellationRequestMessageResponseDto
  | ReviewSubmittedMessageResponseDto
  | DocumentRequestMessageResponseDto
  | ContractRestrictionConfirmMessageResponseDto;

/**
 * List of chat messages with pagination metadata
 */
export interface ChatMessageListResponseDto {
  items: ChatMessageResponseDto[];
  meta: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
  };
}
